import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

interface PageTitleContextValue {
  pageTitle: string | null;
  setPageTitle: (title: string | null) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [pageTitle, setPageTitle] = useState<string | null>(null);

  return <PageTitleContext.Provider value={{ pageTitle, setPageTitle }}>{children}</PageTitleContext.Provider>;
}

export function usePageTitle(): PageTitleContextValue {
  const context = useContext(PageTitleContext);
  if (!context) {
    throw new Error('usePageTitle must be used within a PageTitleProvider');
  }
  return context;
}

/**
 * Hook to register a page title element that will be shown in the TopBar when scrolled out of view.
 * Returns a ref callback to attach to the title element.
 */
export function usePageTitleObserver(title: string): (element: HTMLElement | null) => void {
  const { setPageTitle } = usePageTitle();

  const refCallback = useCallback(
    (element: HTMLElement | null) => {
      if (!element) {
        setPageTitle(null);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          // When the title is not intersecting (scrolled out of view), show it in the TopBar
          if (!entry.isIntersecting) {
            setPageTitle(title);
          } else {
            setPageTitle(null);
          }
        },
        {
          // The root margin accounts for the TopBar height (40px) + some buffer
          rootMargin: '-50px 0px 0px 0px',
          threshold: 0,
        },
      );

      observer.observe(element);

      // Cleanup function - this won't be called by ref callback directly,
      // but we'll handle it when element becomes null
      return () => {
        observer.disconnect();
        setPageTitle(null);
      };
    },
    [setPageTitle, title],
  );

  return refCallback;
}
